import { Reducer } from 'redux';
import { persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { AIRPORTS_SUCCESSFUL, AIRPORTS_FAILURE, AIRPORTS_REQUEST } from 'action-creators/airports';
import { RESET } from 'action-creators/generic';
import { AirportRequestResponse } from 'models/airport';

export enum BySearchType {
  DEPARTURE = 'DEPARTURE',
  ARRIVAL = 'ARRIVAL',
  PICKUP = 'PICKUP',
  DROPOFF = 'DROPOFF',
  DESTINATION = 'DESTINATION',
}

export interface AirportsState {
  isLoading: boolean;
  bySearch: {
    departure: AirportRequestResponse;
    arrival: AirportRequestResponse;
    pickup: AirportRequestResponse;
    dropoff: AirportRequestResponse;
    destination: AirportRequestResponse;
  };
  error: string | null;
}

const initialState: AirportsState = {
  isLoading: false,
  bySearch: {
    departure: { airports: [], cities: [] },
    arrival: { airports: [], cities: [] },
    pickup: { airports: [], cities: [] },
    dropoff: { airports: [], cities: [] },
    destination: { airports: [], cities: [] },
  },
  error: null,
};

export interface AirportPayload extends AirportRequestResponse {
  type?: BySearchType | string;
}

const mapPayloadToState = (payload: AirportPayload): AirportRequestResponse => ({
  airports: payload.airports,
  cities: payload.cities,
});

const reducer: Reducer<AirportsState> = (state = initialState, action) => {
  switch (action.type) {
    case AIRPORTS_REQUEST:
      return {
        ...state,
        isLoading: true,
      };
    case AIRPORTS_SUCCESSFUL:
      if (action.payload.type === BySearchType.ARRIVAL) {
        return {
          ...state,
          bySearch: { ...state.bySearch, arrival: mapPayloadToState(action.payload) },
          isLoading: false,
        };
      }
      if (action.payload.type === BySearchType.DESTINATION) {
        return {
          ...state,
          bySearch: { ...state.bySearch, destination: mapPayloadToState(action.payload) },
          isLoading: false,
        };
      }
      if (action.payload.type === BySearchType.PICKUP) {
        return {
          ...state,
          bySearch: {
            ...state.bySearch,
            pickup: mapPayloadToState(action.payload),
          },
          isLoading: false,
        };
      }
      if (action.payload.type === BySearchType.DROPOFF) {
        return {
          ...state,
          bySearch: {
            ...state.bySearch,
            dropoff: mapPayloadToState(action.payload),
          },
          isLoading: false,
        };
      }
      return {
        ...state,
        bySearch: { ...state.bySearch, departure: mapPayloadToState(action.payload) },
        isLoading: false,
      };
    case AIRPORTS_FAILURE:
      if (action.payload.type === BySearchType.ARRIVAL) {
        return {
          ...state,
          error: action.payload,
          bySearch: {
            ...state.bySearch,
            arrival: { airports: [], cities: [] },
          },
          isLoading: false,
        };
      }
      if (action.payload.type === BySearchType.DESTINATION) {
        return {
          ...state,
          error: action.payload,
          bySearch: {
            ...state.bySearch,
            destination: { airports: [], cities: [] },
          },
          isLoading: false,
        };
      }
      if (action.payload.type === BySearchType.PICKUP) {
        return {
          ...state,
          error: action.payload,
          bySearch: {
            ...state.bySearch,
            pickup: { airports: [], cities: [] },
          },
          isLoading: false,
        };
      }
      if (action.payload.type === BySearchType.DROPOFF) {
        return {
          ...state,
          error: action.payload,
          bySearch: {
            ...state.bySearch,
            dropoff: { airports: [], cities: [] },
          },
          isLoading: false,
        };
      }
      return {
        ...state,
        error: action.payload,
        bySearch: {
          ...state.bySearch,
          departure: { airports: [], cities: [] },
        },
        isLoading: false,
      };
    case RESET:
      return initialState;
    default:
      return state;
  }
};

const migrations = {
  2: (state: any) => ({
    ...state,
    bySearch: {
      ...initialState.bySearch,
      ...state.bySearch,
    },
  }),
};

const persistConfig = {
  storage,
  version: 2,
  key: 'airports',
  blacklist: ['isLoading', 'error'],
  migrate: createMigrate(migrations),
};

export default persistReducer(persistConfig, reducer);
